import {
  reduceSurvey,
  type FunnelSurvey,
  type SurveyAction,
} from '@bbk/retention-sdk/internal';
import {
  type ExperienceMultiRowSurveyCard,
  type ExperienceMultiSurveyCardSection,
} from '@bbkAdminRedux/rtkq/experience.slice';
import {
  SurveyType,
  type RestMultiRowSurveyCard,
} from '@bbkAdminUtils/api-client/rest-cards-types';
import {
  type RestCard,
  type RestExperience,
} from '@bbkAdminUtils/api-client/rest-experiences-api';
import { type RestModalCard } from '@bbkAdminUtils/api-client/rest-modals-cards-types';
import { type SubmissionConfig } from '@bbkFunnel/events';
import { type FunnelModalOnCloseReason } from '@bbkFunnelComponents/modal';

export type FunnelDerivedModel = {
  cards: Map<string, RestCard>;
  cardsByTemplate: Map<string, RestCard>;
  content: RestCard[];
  initialSurvey: Partial<ExperienceMultiRowSurveyCard['provides']>;
  surveyType: SurveyType;
  surveySection: Partial<RestMultiRowSurveyCard>;
  submitConfig: SubmissionConfig;
  questions: ExperienceMultiSurveyCardSection[];
  presentation?: string;
};

type DeriveFunnelModelOptions = {
  normalizeSentimentZero: boolean;
};

export const deriveFunnelModel = (
  { layout }: Pick<RestExperience, 'layout'>,
  { normalizeSentimentZero }: DeriveFunnelModelOptions
): FunnelDerivedModel => {
  const cards = new Map(layout.cards.map((card) => [card.name, card]));
  const cardsByTemplate = new Map(
    layout.cards.map((card) => [card.template, card])
  );
  const surveySection = (cards.get('multi_row_survey') ||
    {}) as Partial<ExperienceMultiRowSurveyCard>;
  const questions = surveySection.requires?.multi_row_survey || [];
  const initialSurvey: Partial<ExperienceMultiRowSurveyCard['provides']> = {
    ...(surveySection.provides || {}),
  };
  const submitConfig = surveySection.requires?.forwards?.sync;

  if (normalizeSentimentZero && initialSurvey.sentiment === 0) {
    initialSurvey.sentiment = -1;
  }

  return {
    cards,
    cardsByTemplate,
    content: layout.cards.filter((card) => card.deploy === 'exit_survey'),
    initialSurvey,
    surveyType: SurveyType.multi_row_survey,
    surveySection,
    submitConfig: submitConfig === undefined ? {} : submitConfig,
    questions,
    presentation: layout.presentation,
  };
};

export { type FunnelSurvey } from '@bbk/retention-sdk/internal';

/**
 * Spelled out rather than inferred from `processCards`: inferring it would make
 * every consumer's type depend on that function's body and degrade inference
 * for all of them.
 */
export type FunnelDerived = Omit<
  FunnelDerivedModel,
  'initialSurvey' | 'surveySection'
> & {
  survey: FunnelSurvey;
  surveySection: RestMultiRowSurveyCard;
};

/**
 * Always normalizes a configured `sentiment: 0`, which used to depend on the
 * caller. The preview cards-change reset was the one caller that skipped it,
 * and got away with it only because the next render re-derived and normalized.
 * Nothing re-derives on the way out any more.
 */
export const processCards = (
  props: Pick<RestExperience, 'layout'>
): FunnelDerived => {
  const { initialSurvey, surveySection, ...derivedModel } = deriveFunnelModel(
    props,
    {
      normalizeSentimentZero: true,
    }
  );

  // Preserve the class component's historical empty-object sentinel while
  // keeping the extracted model honest about a missing survey card.
  return {
    ...derivedModel,
    survey: initialSurvey as FunnelSurvey,
    surveySection: surveySection as RestMultiRowSurveyCard,
  };
};

export type FunnelDerivedCache = {
  layout: RestExperience['layout'];
  cards: RestExperience['layout']['cards'];
  model: FunnelDerived;
};

/**
 * Reads the model derived from `layout`, rebuilding it only when the caller is
 * allowed to. Caching is load-bearing rather than an optimization: `cards`,
 * `cardsByTemplate` and `content` are a Map, a Map and an array handed straight
 * to children, so deriving per render would give children new identities on
 * every state update.
 *
 * Only preview follows `layout`. Outside preview the model stays pinned to the
 * layout the caller first read with, because `funnel-loader` re-fetches every
 * 24 hours to renew the integrity token and re-renders the same unkeyed
 * element; adopting that layout would strand the answers already in session
 * state against new cards, questions and a new submit config.
 *
 * `cards` is part of the preview key even though callers replace the whole
 * layout tree, because the preview reset triggers on `layout.cards`; keying on
 * less than it does would let that reset dispatch a survey derived from the
 * previous cards.
 *
 * Returns the cache to store rather than mutating one, so the caller owns where
 * it lives — a class field, a ref, whatever.
 */
export const readDerivedModel = (
  cache: FunnelDerivedCache | undefined,
  props: Pick<RestExperience, 'layout'>,
  isPreview: boolean | undefined
): { model: FunnelDerived; cache: FunnelDerivedCache } => {
  const { layout } = props;

  if (
    !cache ||
    (isPreview && (cache.layout !== layout || cache.cards !== layout.cards))
  ) {
    const rebuilt = {
      layout,
      cards: layout.cards,
      model: processCards(props),
    };
    return { model: rebuilt.model, cache: rebuilt };
  }

  return { model: cache.model, cache };
};

/**
 * The mutable part of a funnel session: everything that changes because of user
 * interaction rather than because of props. Values derived from `layout` belong
 * in `FunnelDerivedModel`, and `FunnelLocalState` is an external storage adapter
 * rather than session state, so neither is represented here.
 *
 * This reducer is the only writer. Preview still re-derives the survey from
 * props on every render, but that now happens on read, through
 * `mergePreviewSurvey`, so it composes with state instead of overwriting it.
 */
export type FunnelSessionState = {
  showModal: boolean;
  showLoader: boolean;
  showOpaqueLoader: boolean;
  closeModalReason?: FunnelModalOnCloseReason;
  /** `undefined` once an action selects a modal the experience never configured. */
  modal: RestModalCard | undefined;
  survey: FunnelSurvey;
  hasShownRefundModal: boolean;
};

export type FunnelSavedSessionState = Partial<
  Pick<FunnelSessionState, 'survey' | 'modal' | 'showModal'>
>;

export type FunnelAction =
  | { type: 'hydrateSavedState'; savedState: FunnelSavedSessionState }
  | SurveyAction
  // Editing the experience swaps in new cards, which discards the answers given
  // against the old ones. The reducer sees only state and action, so it cannot
  // derive the replacement from props: the caller passes the already-normalized
  // survey, which is also where `normalizeSentimentZero` has to be applied.
  | { type: 'resetSurveyForPreviewCards'; survey: FunnelSurvey }
  // `cards.get` misses whenever an action names a modal the experience does not
  // configure. That absence is load-bearing: it is what drives the
  // unconfigured-modal recovery path, so it is modelled rather than cast away.
  | { type: 'selectModal'; modal: RestModalCard | undefined }
  | { type: 'openModal' }
  | { type: 'closeModal'; reason?: FunnelModalOnCloseReason }
  | { type: 'hideUnconfiguredModal' }
  | { type: 'showRefundModal'; modal: RestModalCard }
  | { type: 'markRefundModalShown' }
  | { type: 'showFullLoader' }
  | { type: 'showOpaqueLoader' }
  | { type: 'hideOpaqueLoader' };

export const createInitialSessionState = (
  initialSurvey: FunnelSurvey
): FunnelSessionState => ({
  showModal: false,
  showLoader: false,
  showOpaqueLoader: false,
  modal: {} as RestModalCard,
  survey: initialSurvey,
  hasShownRefundModal: false,
});

/**
 * Reconciles the survey preview re-derives from props with the answers already
 * held in state. Reasons survive because the class shared one array between
 * props and state and appended to it in place, so re-deriving handed the same
 * populated array straight back; without that aliasing a preview click would
 * leave the radio unselected. The remaining answers are scalars, which the
 * re-derivation has always reset.
 *
 * Only for re-derivation from unchanged props. Editing the experience swaps in
 * new cards, which resets the survey through `resetSurveyForPreviewCards`.
 */
export const mergePreviewSurvey = (
  derived: FunnelSurvey,
  current: FunnelSurvey | undefined
): FunnelSurvey => ({
  ...derived,
  reason_for_leaving:
    current?.reason_for_leaving ?? derived.reason_for_leaving ?? [],
});

/**
 * Preview re-derives the survey from props on every render, which is why scalar
 * answers reset instantly in the editor while reason selections survive;
 * `mergePreviewSurvey` holds that reconciliation rule. Outside preview the
 * reducer owns the survey outright.
 *
 * Every survey read goes through here. Reading session state directly would
 * skip the merge and change what preview shows.
 */
export const readSurvey = (
  isPreview: boolean | undefined,
  derivedSurvey: FunnelSurvey,
  stateSurvey: FunnelSurvey
): FunnelSurvey =>
  isPreview ? mergePreviewSurvey(derivedSurvey, stateSurvey) : stateSurvey;

export const funnelSessionReducer = (
  state: FunnelSessionState,
  action: FunnelAction
): FunnelSessionState => {
  switch (action.type) {
    case 'hydrateSavedState':
      return { ...state, ...action.savedState };

    case 'setSurveyField':
    case 'setReasonForLeaving':
    case 'clearCompetitionOther':
    case 'clearReasonOther':
    case 'setModalResponse':
      return { ...state, survey: reduceSurvey(state.survey, action) };

    case 'resetSurveyForPreviewCards':
      return { ...state, survey: action.survey };

    case 'selectModal':
      return { ...state, modal: action.modal };

    case 'openModal':
      return { ...state, showModal: true };

    case 'closeModal':
      return {
        ...state,
        closeModalReason: action.reason,
        showModal: false,
        modal: {} as RestModalCard,
      };

    // Raised when the configured modal cannot be found. Unlike `closeModal`
    // this must leave the selected modal and the previous close reason alone.
    case 'hideUnconfiguredModal':
      return { ...state, showModal: false };

    case 'showRefundModal':
      return { ...state, modal: action.modal, showModal: true };

    case 'markRefundModalShown':
      return { ...state, hasShownRefundModal: true };

    case 'showFullLoader':
      return { ...state, showLoader: true };

    case 'showOpaqueLoader':
      return { ...state, showOpaqueLoader: true };

    case 'hideOpaqueLoader':
      return { ...state, showOpaqueLoader: false };

    default: {
      const unhandled: never = action;
      throw new Error(
        `Unhandled funnel session action: ${JSON.stringify(unhandled)}`
      );
    }
  }
};
